import { Alert } from "@material-ui/lab";
import { EditEntityDialogButton } from "components/dialogs/EditEntityDialogButton";
import { GridForm, GridInput } from "components/GridForm";
import { NumberInputEx } from "components/inputs";
import { OrderStatusCompleted } from "entities/OrderStatus";
import React from "react";
import {
	TextInput,
	maxLength,
	required,
	useRecordContext,
	useTranslate,
} from "react-admin";
import { useForm } from "react-final-form";
import { isNullUndEmpty } from "utils/general";
import { roundToTwo } from "utils/math";
import { validatePositiveNumber } from "utils/validation/general";

const TEXT_FIELDS = ["product_number", "product_name"];

const getBoxWeight = (netWeight, quantity) =>
	quantity > 0 && netWeight ? roundToTwo(netWeight / quantity) : 0;

const calcNetWeight = (boxWeight, quantity) => {
	if (isNullUndEmpty(boxWeight) || isNullUndEmpty(quantity)) return undefined;
	const netWeight = roundToTwo(Number(boxWeight) * Number(quantity));
	return Number.isFinite(netWeight) ? netWeight : undefined;
};

const PalletInfoFormFields = ({ translate, disableWeightFields }) => {
	const form = useForm();

	const recalcNetWeight = (boxWeight, quantity) => {
		form.change("net_weight", calcNetWeight(boxWeight, quantity));
	};

	const handleQuantityChange = (event) => {
		recalcNetWeight(form.getState().values.box_weight, event?.target?.value);
	};

	const handleBoxWeightChange = (event) => {
		recalcNetWeight(event?.target?.value, form.getState().values.quantity);
	};

	return (
		<GridForm>
			<GridInput
				md={12}
				component={TextInput}
				source="product_number"
				label={translate("tab.pallets.product_number")}
				validate={[required(), maxLength(64)]}
			/>
			<GridInput
				md={12}
				component={TextInput}
				source="product_name"
				label={translate("tab.pallets.product_name")}
				validate={[required(), maxLength(256)]}
			/>
			<GridInput
				md={4}
				component={NumberInputEx}
				source="quantity"
				label={translate("tab.pallets.quantity")}
				validate={[required(), validatePositiveNumber(translate)]}
				step={1}
				onChange={handleQuantityChange}
				disabled={disableWeightFields}
			/>
			<GridInput
				md={4}
				component={NumberInputEx}
				source="box_weight"
				label={translate("tab.pallets.weight_box")}
				validate={[required(), validatePositiveNumber(translate)]}
				helperText={translate("osfc.dialogs.pallets.box_weight_hint")}
				type="float"
				digitsCount={2}
				step={0.5}
				onChange={handleBoxWeightChange}
				disabled={disableWeightFields}
			/>
			<GridInput
				md={4}
				component={NumberInputEx}
				source="net_weight"
				label={translate("tab.pallets.weight_net")}
				validate={[required(), validatePositiveNumber(translate)]}
				type="float"
				digitsCount={2}
				disabled
			/>
		</GridForm>
	);
};

export const EditPalletInfoDialog = ({ order, disabled = false }) => {
	const pallet = useRecordContext();
	const translate = useTranslate();
	const isReservedByAnotherOrder =
		!!pallet.reserved_order_id && pallet.reserved_order_id !== pallet.order_id;
	// mirrors backend ensure_pallet_has_no_box_movements (boxes moved OFF this pallet) — any edit is rejected there
	const hasBoxesMovedOff = !!pallet.boxes?.some((box) => box.is_moved);
	const isDisabled = disabled || isReservedByAnotherOrder || !!pallet.is_target || hasBoxesMovedOff;
	// mirrors backend ensure_pallet_weight_fields_editable (box records originated on this pallet)
	const hasBoxRecords = !!pallet.boxes?.some((box) => box.original_pallet_id === pallet.id);
	const isCompletedOrderCorrection = OrderStatusCompleted.includes(order?.status);

	const initialBoxWeight = getBoxWeight(pallet.net_weight, pallet.quantity);

	const buildChangedFields = (data) => {
		const changed = {};

		TEXT_FIELDS.forEach((field) => {
			if (data[field] !== pallet[field]) {
				changed[field] = data[field];
			}
		});

		// compare user inputs, not derived net_weight (box_weight rounding would fake a change)
		const isWeightChanged =
			data.quantity !== pallet.quantity || Number(data.box_weight) !== initialBoxWeight;
		if (isWeightChanged) {
			changed.quantity = data.quantity;
			changed.net_weight = Number(data.net_weight);
		}

		return changed;
	};

	const initialValues = {
		id: pallet.id,
		product_number: pallet.product_number,
		product_name: pallet.product_name,
		quantity: pallet.quantity,
		box_weight: initialBoxWeight,
		net_weight: pallet.net_weight,
	};

	return (
		<EditEntityDialogButton
			resource="pallets"
			record={initialValues}
			disabled={isDisabled}
			titleKey="osfc.dialogs.pallets.edit_info.title"
			successMessageKey="osfc.dialogs.pallets.update_info.success"
			prepareValues={buildChangedFields}
		>
			{() => (
				<>
					{isCompletedOrderCorrection && (
						<Alert severity="warning" style={{ marginBottom: 12 }}>
							{translate("osfc.dialogs.pallets.edit_info.completed_warning")}
						</Alert>
					)}
					<PalletInfoFormFields translate={translate} disableWeightFields={hasBoxRecords} />
				</>
			)}
		</EditEntityDialogButton>
	);
};
